/**
 * Mi Boleta — Fila Virtual / Sala de Espera (Flash Sales).
 *
 * Pantalla tranquilizadora que retiene al fan mientras el sistema le asigna turno
 * en una venta de alta demanda. El estado de la fila (posición, ETA, transición de
 * salida, guarda de navegación) vive en `useFlashSaleQueue`; este componente solo
 * lo presenta. La fila es REAL: `useFlashSaleQueue` la consulta contra el motor
 * de `api-core` (migración 0087) cada 5s.
 *
 * Estilos: CSS Module con tokens del Product Design System (Sora + Mi Blue),
 * aislado del entorno global de commerce. Ver cabecera de `WaitingRoom.module.css`.
 */
import type { FlashSaleQueueState } from '@/hooks/useFlashSaleQueue';

import styles from './WaitingRoom.module.css';

export interface WaitingRoomProps {
  /**
   * Estado de la fila, resuelto por quien monta la sala.
   *
   * Antes el componente llamaba a `useFlashSaleQueue` por su cuenta, y por eso
   * no había forma de que la RUTA reaccionara a un 401: la sala se pintaba
   * igual, diciéndole "vas en fila" a alguien sin sesión. Ahora el estado entra
   * por props y la ruta decide qué hacer con él.
   */
  queue: FlashSaleQueueState;
  /** Nombre del evento/venta, para personalizar el título. */
  eventTitle?: string;
  className?: string;
}

/** `1234` → `1.234` (separador de miles es-CO, sin depender de Intl/locale). */
function groupThousands(n: number): string {
  return String(Math.max(0, Math.round(n))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** ETA legible: `> 60s` → «X min»; si no, «X s». */
function formatEta(seconds: number): string {
  if (seconds >= 60) return `${Math.ceil(seconds / 60)} min`;
  return `${Math.max(1, seconds)} s`;
}

export function WaitingRoom({ queue, eventTitle, className }: WaitingRoomProps) {
  const { usersAhead, estimatedSeconds, status } = queue;

  const rootClass = className ? `${styles.root} ${className}` : styles.root;

  // ── Transición de salida: turno asignado ──────────────────────────
  if (status === 'ready') {
    return (
      <section className={rootClass} aria-label="Turno asignado">
        <div className={styles.check} role="img" aria-label="Turno asignado">
          <svg viewBox="0 0 52 52" aria-hidden="true" className={styles.checkSvg}>
            <circle className={styles.checkRing} cx="26" cy="26" r="24" />
            <path className={styles.checkMark} d="M16 27 l7 7 l13 -15" />
          </svg>
        </div>
        <h1 className={styles.title}>¡Es tu turno!</h1>
        <p className={styles.subtitle}>Te estamos llevando al checkout. No cierres esta pestaña.</p>
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-label="Redirigiendo al checkout"
          aria-valuetext="Redirigiendo"
        >
          <div className={styles.progressBar} />
        </div>
      </section>
    );
  }

  // ── Espera ─────────────────────────────────────────────────────────
  const title = eventTitle ? `Vas en fila para ${eventTitle}` : 'Estás en la fila virtual';

  return (
    <section className={rootClass} aria-label="Sala de espera">
      {/* Loader circular (indicador visual principal) */}
      <div className={styles.loader} role="img" aria-label="Procesando tu turno">
        <svg className={styles.loaderSvg} viewBox="0 0 100 100" aria-hidden="true">
          <circle className={styles.loaderTrack} cx="50" cy="50" r="42" />
          <circle className={styles.loaderArc} cx="50" cy="50" r="42" />
        </svg>
        <span className={styles.loaderPulse} aria-hidden="true">
          MB
        </span>
      </div>

      <h1 className={styles.title}>{title}</h1>
      <p className={styles.subtitle}>
        Por favor, no recargues la página. Tu turno se asignará automáticamente.
      </p>

      {/* Barra de progreso indeterminada — refuerza la sensación de avance */}
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-label="Avance de la fila"
        aria-valuetext="En progreso"
      >
        <div className={styles.progressBar} />
      </div>

      {/* Estado en vivo (viene del hook: polling cada 5s) */}
      <div className={styles.stats} aria-live="polite">
        <div className={styles.stat}>
          <span className={styles.statLabel}>Usuarios delante de ti</span>
          <span className={styles.statValue}>{groupThousands(usersAhead)}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Tiempo estimado</span>
          <span className={styles.statValue}>{formatEta(estimatedSeconds)}</span>
        </div>
      </div>

      <p className={styles.foot}>
        <span className={styles.dot} aria-hidden="true" />
        Mantené esta pestaña abierta · te movemos automáticamente
      </p>
    </section>
  );
}
